import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { ArrowRight, Check, Circle, Clock, Loader2, Upload } from 'lucide-react';
import { API } from '../../lib/api';
import { useSeo } from './site/ui';
import { FilePicker, uploadAll } from './site/SwitchFiles';

const STATE = {
  done: ['Done', 'bg-[#0f766e] text-[#ffffff]', Check],
  in_progress: ['In progress', 'bg-[#f59e0b] text-[#ffffff]', Clock],
  todo: ['To do', 'bg-[#e2e8f0] text-[#64748b]', Circle],
};
const BIG = { new: 'Request received', in_progress: 'Switch in progress', live: 'You’re live on Driveline' };
const inr = (n) => `₹ ${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const fmt = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : 'To be agreed on the call');

const Steps = ({ steps }) => (
  <ol className="relative space-y-5 border-l-2 border-[#e2e8f0] pl-6" data-testid="track-steps">
    {steps.map((s) => {
      const [label, cls, Icon] = STATE[s.state] || STATE.todo;
      return (
        <li key={s.key} className="relative" data-testid={`track-step-${s.key}`} data-state={s.state}>
          <span className={`absolute -left-[37px] flex h-6 w-6 items-center justify-center rounded-full ${cls}`}><Icon size={13} /></span>
          <p className="font-semibold text-[#0f172a]">{s.label}</p><p className="text-xs text-[#64748b]">{label}</p>
        </li>
      );
    })}
  </ol>
);

const Results = ({ r }) => {
  const parts = [
    r.customers && `${r.customers.customers_created} customers${r.customers.customers_merged ? ` (+${r.customers.customers_merged} merged)` : ''}, ${r.customers.vehicles_added} vehicles`,
    r.balances && `${inr(r.balances.total_due)} open dues across ${r.balances.customers_with_dues} customers`,
    r.stock && `${r.stock.parts_created + r.stock.parts_updated} parts`,
  ].filter(Boolean);
  const issues = ['customers', 'balances', 'stock'].flatMap((k) => (r[k]?.issues || []).map((x) => `${x.issue} (row ${x.row})`));
  if (!parts.length) return null;
  return (
    <div className="rounded-3xl bg-[#ffffff] p-6" data-testid="track-results">
      <p className="text-xs font-semibold uppercase tracking-wider text-[#64748b]">Imported so far</p>
      <p className="dl-display mt-2 text-xl font-semibold text-[#0f172a]" data-testid="track-results-summary">{parts.join(' · ')}</p>
      {issues.length > 0 && (
        <details className="mt-3 text-sm" data-testid="track-issues"><summary className="cursor-pointer font-semibold text-[#b45309]">{issues.length} row(s) need a look</summary>
          <ul className="mt-2 max-h-48 list-disc space-y-1 overflow-auto pl-5 text-[#475569]">{issues.slice(0, 100).map((x) => <li key={x}>{x}</li>)}</ul></details>
      )}
    </div>
  );
};

const AddFiles = ({ code, onDone }) => {
  const [files, setFiles] = useState([]);
  const [progress, setProgress] = useState({});
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    await uploadAll(files, `${API}/public/driveline/switch/${code}/files`, setProgress);
    setBusy(false); setFiles([]); setProgress({}); onDone();
  };
  return (
    <div className="space-y-3" data-testid="track-add-files">
      <FilePicker files={files} setFiles={setFiles} busy={busy} progress={progress} />
      {files.length > 0 && <button type="button" onClick={send} disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-[#0f766e] px-5 py-2.5 text-sm font-semibold text-[#ffffff] disabled:opacity-60" data-testid="track-upload-btn">
        {busy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Upload {files.length} file(s)</button>}
    </div>
  );
};

export default function DrivelineSwitchTrack() {
  const { code } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  useSeo({ title: 'Your Driveline switch tracker', description: 'Follow each step of your move to Driveline.' });
  const load = useCallback(() => axios.get(`${API}/public/driveline/switch/${code}`).then((r) => setD(r.data)).catch(() => setErr('We could not find that switch request. Check the link in your email or WhatsApp.')), [code]);
  useEffect(() => { load(); }, [load]);

  if (err) return <p className="mx-auto max-w-xl px-5 pb-24 pt-40 text-center text-[#475569]" data-testid="track-error">{err}</p>;
  if (!d) return <div className="pt-40 text-center"><Loader2 className="inline animate-spin text-[#0f766e]" /></div>;
  return (
    <div className="mx-auto max-w-5xl px-5 pb-24 pt-32 sm:px-8" data-testid="track-page">
      <p className="text-xs font-semibold uppercase tracking-wider text-[#0f766e]">{d.workshop_name}</p>
      <h1 className="dl-display mt-2 text-4xl font-semibold tracking-tight text-[#0f172a] sm:text-5xl" data-testid="track-status">{BIG[d.status]}</h1>
      <p className="mt-3 text-[#475569]">Go-live date: <b className="text-[#0f172a]" data-testid="track-golive">{fmt(d.go_live_date)}</b></p>
      {d.team_note && <div className="mt-5 rounded-2xl border-l-4 border-[#0f766e] bg-[#ffffff] p-4 text-[#334155]" data-testid="track-note"><p className="text-xs font-semibold uppercase tracking-wider text-[#64748b]">Note from the Driveline team</p><p className="mt-1 whitespace-pre-wrap">{d.team_note}</p></div>}
      {d.status === 'live' && (
        <div className="mt-5 rounded-3xl bg-[#0b1220] p-6 text-[#e2e8f0]" data-testid="track-live">
          <p className="dl-display text-xl font-semibold text-[#f8fafc]">How to start</p>
          <ol className="mt-3 list-decimal space-y-1 pl-5 text-sm [&>li]:text-[#e2e8f0]"><li>Sign in and check your customers and stock.</li><li>Raise your first invoice from a job card.</li><li>Send it on WhatsApp in one tap.</li></ol>
          <a href={d.login_url || '/login'} className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#5eead4] px-5 py-2.5 text-sm font-semibold text-[#0b1220]" data-testid="track-login">Sign in to Driveline <ArrowRight size={15} /></a>
        </div>
      )}
      <div className="mt-10 grid gap-8 lg:grid-cols-[1fr_1.2fr]">
        <div className="rounded-3xl bg-[#ffffff] p-6 pl-10"><Steps steps={d.steps} /></div>
        <div className="space-y-6">
          <Results r={d.results} />
          <div className="rounded-3xl bg-[#ffffff] p-6" data-testid="track-checklist">
            <p className="text-xs font-semibold uppercase tracking-wider text-[#64748b]">Checklist</p>
            <ul className="mt-3 space-y-2">{d.checklist.map((c) => (
              <li key={c.key} className="flex items-center justify-between gap-3 text-sm" data-testid={`track-check-${c.key}`}>
                <span className="text-[#0f172a]">{c.label}</span>
                <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold ${c.received ? 'bg-[#ccfbf1] text-[#0f766e]' : 'bg-[#fef3c7] text-[#b45309]'}`}>{c.received ? 'Received' : 'Still needed'}</span>
              </li>))}</ul>
            <p className="mt-5 text-sm font-semibold text-[#0f172a]">Add files</p>
            <p className="mb-3 text-xs text-[#64748b]">{d.files.length} file(s) received so far.</p>
            <AddFiles code={code} onDone={load} />
          </div>
        </div>
      </div>
    </div>
  );
}
